import type { ModuleCategory, ModuleKey, ModuleStatus } from "@how/types";

export interface ModuleDefinition {
  readonly key: ModuleKey;
  readonly displayName: string;
  readonly description: string;
  readonly category: ModuleCategory;
  readonly status: ModuleStatus;
  readonly isBase: boolean;
  readonly dependencies: readonly ModuleKey[];
  readonly sortOrder: number;
}

const base = (
  key: ModuleKey,
  displayName: string,
  description: string,
  category: ModuleCategory,
  sortOrder: number,
): ModuleDefinition => ({
  key,
  displayName,
  description,
  category,
  status: "active",
  isBase: true,
  dependencies: [],
  sortOrder,
});

const addon = (
  key: ModuleKey,
  displayName: string,
  description: string,
  category: ModuleCategory,
  sortOrder: number,
  dependencies: readonly ModuleKey[] = [],
  // "beta" while a module's staff half is built and its member half is not (ADR 0037); "active" when both ship.
  status: ModuleStatus = "planned",
): ModuleDefinition => ({
  key,
  displayName,
  description,
  category,
  status,
  isBase: false,
  dependencies,
  sortOrder,
});

/** Single source of truth for platform_modules. Changing this file requires `pnpm catalog:generate`. */
export const MODULES: readonly ModuleDefinition[] = [
  base(
    "tenant_admin",
    "Church administration",
    "Settings, users, roles, domains, audit log.",
    "core",
    10,
  ),
  base(
    "core_crm",
    "Core CRM",
    "People, households, contacts, tags, notes, activities.",
    "people",
    20,
  ),
  base(
    "website",
    "Website and content",
    "Public site, structured content, pages, navigation, forms, SEO.",
    "core",
    30,
  ),
  base(
    "member_portal",
    "Member portal",
    "Member self-service for profile, household, preferences.",
    "people",
    40,
  ),
  // active since 2026-10-02 (owner, #303): the member's half ("Where I am" on the portal's Home, my_pathway) shipped
  // with Milestone 3 task 14 (ADR 0037).
  addon(
    "membership",
    "Membership",
    "Membership pathways, steps, and records.",
    "people",
    100,
    ["core_crm"],
    "active",
  ),
  // active since Milestone 3 task 5 shipped the member's half (ADR 0040, ADR 0037).
  addon(
    "groups",
    "Groups and serving",
    "Groups, leaders, serve teams, participation.",
    "people",
    110,
    ["core_crm"],
    "active",
  ),
  // active since Milestone 3 task 8 shipped the member's half (ADR 0041, ADR 0037).
  addon(
    "events",
    "Events",
    "Events, registrations, attendance.",
    "operations",
    120,
    ["core_crm"],
    "active",
  ),
  // beta: the model and the station ship in Milestone 3 tasks 9–10 (ADR 0042); the guardian's half waits on counsel.
  addon(
    "event_checkin",
    "Event check-in",
    "Check-in sessions and printing.",
    "operations",
    130,
    ["events"],
    "beta",
  ),
  // active since 2026-09-30 (owner, #275): the staff grid and the member's half shipped in Milestone 3 tasks 12–13
  // (ADR 0043, ADR 0037). Called Services since 2026-10-08 (ADR 0069), as churches and Planning Center call it.
  addon(
    "services",
    "Services",
    "Service plans, teams, positions, serving requests, availability, reminders.",
    "operations",
    140,
    ["core_crm"],
    "active",
  ),
  addon(
    "sms_messaging",
    "SMS messaging",
    "Consent-aware SMS through platform-managed Twilio subaccounts.",
    "communications",
    200,
    ["core_crm"],
  ),
  addon(
    "email_messaging",
    "Email messaging",
    "Consent-aware email campaigns and delivery.",
    "communications",
    210,
    ["core_crm"],
  ),
  addon(
    "commerce",
    "Commerce and store",
    "Catalog, carts, orders, fulfillment, refunds through the church's Stripe account.",
    "commerce",
    300,
    ["core_crm"],
    "beta",
  ),
  // Active since Milestone 5 task A3 (ADR 0061): staff manage the library, visitors watch and listen on the website
  // and the podcast, and members browse and play sermons in the portal (ADR 0037's member half).
  addon(
    "sermons_media",
    "Sermons and media",
    "Sermon library with series, speakers, video, audio, notes and a podcast feed.",
    "core",
    310,
    ["website"],
    "active",
  ),
  // Devotionals (ADR 0077, owner 2026-10-13): staff write dated devotionals and schedule them ahead; visitors read
  // today's and the archive on the website (/devotional, the "Today's devotional" block), and members read them in the
  // portal (ADR 0037's member half). Both halves ship, so it is active.
  addon(
    "devotionals",
    "Devotionals",
    "Dated daily devotionals with scripture, a reflection and a prayer, on the website and in the member portal.",
    "core",
    320,
    ["website"],
    "active",
  ),
  // Active since 2026-10-06 (owner): the four report functions (ADR 0052) and their screens (#418) are built. Staff
  // only: members have no half here, which ADR 0037 allows for a staff tool.
  addon(
    "advanced_reporting",
    "Advanced reporting",
    "Attendance, participation, registrations and messaging reports by campus and date range; audited exports.",
    "platform",
    400,
    ["core_crm"],
    "active",
  ),
  // Beta since Milestone 5 task B1 (ADR 0036 amendment): the database half of campus-limited staff, a member's own
  // campus choice and the campus page are built; "active" when the developer's screens ship (ADR 0037).
  addon(
    "multi_campus",
    "Multi-campus",
    "A page per campus, the location chooser, and staff limited to their campuses.",
    "platform",
    410,
    [],
    "beta",
  ),
  // Beta since Milestone 5 task B3 (ADR 0053): keys, the api function, subscriptions and the worker are built; the
  // screens follow. A key is a passwordless login with roles, so the API needs no permission model of its own.
  addon(
    "api_access",
    "API access",
    "Per-church API keys that read and write through the same rules as staff.",
    "platform",
    420,
    [],
    "beta",
  ),
  addon(
    "webhooks",
    "Outbound webhooks",
    "Signed announcements of changes to a church's own endpoints, retried for five days.",
    "platform",
    430,
    [],
    "beta",
  ),
  addon(
    "integrations",
    "Integration connectors",
    "Third-party connectors using the church's own accounts.",
    "platform",
    440,
    [],
    "beta",
  ),
  addon("custom_workflows", "Custom workflows", "Automation and workflow engine.", "platform", 450),
];

export function getModule(key: ModuleKey): ModuleDefinition {
  const found = MODULES.find((m) => m.key === key);
  if (!found) throw new Error(`unknown module ${key}`);
  return found;
}

/** Transitive dependencies of a module, sorted, excluding the module itself. */
export function resolveModuleDependencies(key: ModuleKey): ModuleKey[] {
  const seen = new Set<ModuleKey>();
  const visit = (k: ModuleKey): void => {
    for (const dep of getModule(k).dependencies) {
      if (!seen.has(dep)) {
        seen.add(dep);
        visit(dep);
      }
    }
  };
  visit(key);
  return [...seen].sort();
}
